/**
 * WHAT:
 *   Component for displaying parsed PDF data in a simple grid format.
 *
 * WHY:
 *   Centralizes parsed data display UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - TypeScript types (ParsedDataDisplayProps)
 *
 * RISKS:
 *   - NOTE: Large data objects may cause performance issues during rendering
 *   - NOTE: Grid layout may not work correctly with deeply nested objects
 */

import type { ParsedDataDisplayProperties } from "../types";

import { type FC } from "react";

/**
 * WHAT:
 *   Component displaying parsed PDF data in a grid format.
 *
 * WHY:
 *   Encapsulates parsed data display UI logic.
 *
 * @param root0
 *
 * @param root0.parsedData
 *
 * @returns {JSX.Element | null} The ParsedDataDisplay component or null if no data
 *
 */
export const ParsedDataDisplay: FC<ParsedDataDisplayProperties> = ({ parsedData }) => {
  // Reason: Type assertion needed for parsedData.success access
  const parsedDataTyped = parsedData as null | undefined | { success?: boolean };
  if (!parsedDataTyped || parsedDataTyped.success || !parsedData) {
    return null;
  }

  // Reason: After null check, parsedData is guaranteed to exist
  const safeParsedData = parsedData;
  return (
    <div className="mt-6 p-4 bg-slate-600/30 rounded-lg">
      <h3 className="text-lg font-semibold text-white mb-4">Parsed Data</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(safeParsedData).map(([key, value]) => (
          <div className="flex justify-between" key={key}>
            <span className="text-slate-400 capitalize">{key}:</span>
            <span className="text-white font-medium truncate ml-2">{String(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
